import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { isMcpOAuthRequestId } from '@/api/mcp-oauth'
import { useAuth } from '@/hooks/use-auth'
import { useRouter } from '@/hooks/use-router'
import { paths } from '@/paths'
import {
  getReturnUrlFromSearch,
  getSafeDocumentsReturnUrl,
} from '@/utils/auth-return-url'

interface GuestGuardProps {
  children: ReactNode
}

export const GuestGuard: FC<GuestGuardProps> = (props) => {
  const { children } = props
  const { isAuthenticated } = useAuth()
  const router = useRouter()
  const location = useLocation()
  const [checked, setChecked] = useState<boolean>(false)

  const check = useCallback(() => {
    if (isAuthenticated) {
      const mcpRequest = new URLSearchParams(location.search).get('mcpRequest')
      if (isMcpOAuthRequestId(mcpRequest)) {
        router.replace(
          `${paths.oauth.mcpConsent}?request=${encodeURIComponent(mcpRequest)}`
        )
        return
      }
      const returnUrl = getReturnUrlFromSearch(location.search)
      const destination = getSafeDocumentsReturnUrl(
        returnUrl,
        paths.dashboard.index
      )
      router.replace(destination)
    } else {
      setChecked(true)
    }
  }, [isAuthenticated, location.search, router])

  // Only check on mount, this allows us to redirect the user manually when auth state changes
  useEffect(
    () => {
      check()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (!checked) {
    return null
  }

  // If got here, it means that the redirect did not occur, and that tells us that the user is
  // not authenticated / authorized.

  return <>{children}</>
}
